import React, { useEffect, useRef, useState } from 'react';

import { Spinner, useTheme } from '@vw-marketing/us-components';

import { DealerCard } from '../DealerCard/DealerCard';
import { Dealer, DealersList } from '../../../../shared/typings/main.d';
import { getSelectedMarkerIcon, getUnselectedMarkerIcon } from './markerIcons';
import { StyledDealerCardWrapper, StyledMapContainer, StyledMapRefContainer, StyledMapWrapper } from './styles';
import { useGoogleMap } from '../../../../shared/hooks/use-google-map';
import { useGoogleMapsApi } from '../../../../shared/hooks/use-google-maps-api';
import { useMapInitialization } from '../../../../shared/hooks/use-map-initialization';
import { useMapMarkers } from '../../../../shared/hooks/use-map-markers';
import GoogleMapsStyles from './GoogleMapsStyles.json';
import { NoDealersFound } from '../NoDealersFound/NoDealersFound';

export interface ManageDealerMapViewProps {
  authService: any;
  dealersList: DealersList[];
  fetchDealersListByZipCode: (zipCode: string) => Promise<void>;
  userPreferredDealer?: Dealer;
  isLoadingDealers: boolean;
  noDealersFound?: boolean;
  selectedDealerIndex?: number;
  onDealerSelect?: () => void;
  translationHandler: (key: string) => string;
  trackingManager: any;
  localeCode: string;
  locale: string;
  fetchPreferredDealer: (vehicleId: string, vin: string, newDealerId?: string) => Promise<void>;
  showToast: (message: any, appearance?: any, title?: string) => void;
  userPreferredVehicle: any;
  history: any;
  currentPathname: string;
  baseUrl?: string;
}

const DEFAULT_CENTER = { lat: 34.0337, lng: -118.271 };
const DEFAULT_ZIPCODE = '90210';
const CA_DEFAULT_CENTER = { lat: 45.397751, lng: -75.683439 };
const CA_DEFAULT_ZIPCODE = 'K1S3W7';
const IS_LOCAL_DEV = window.location.hostname === 'local.vwmyvwui.vw.com';

export const ManageDealerMapView = ({
  authService,
  dealersList,
  fetchDealersListByZipCode,
  userPreferredDealer,
  isLoadingDealers,
  noDealersFound = false,
  selectedDealerIndex = 0,
  onDealerSelect,
  translationHandler,
  trackingManager,
  localeCode,
  locale,
  fetchPreferredDealer,
  showToast,
  userPreferredVehicle,
  history,
  currentPathname,
  baseUrl
}: ManageDealerMapViewProps) => {
  const [selectedDealer, setSelectedDealer] = useState<DealersList | null>(null);
  const [selectedMarkerIndex, setSelectedMarkerIndex] = useState<number>(selectedDealerIndex);
  const theme = useTheme();

  const mapRef = useRef<HTMLDivElement>(null);

  const { googleAPILoaded } = useGoogleMapsApi({ authService, localeCode, baseUrl });

  const selectedIcon = getSelectedMarkerIcon(theme.colors.button.primary.default, theme.colors.content.inverted, theme.colors.signal.error);
  const unselectedIcon = getUnselectedMarkerIcon(theme.colors.button.primary.default, theme.colors.content.inverted);

  const isCA = localeCode?.includes('CA');

  const { mapCenter, isInitializing } = useMapInitialization({
    googleAPILoaded,
    dealersList,
    fetchDealersListByZipCode,
    defaultCenter: isCA ? CA_DEFAULT_CENTER : DEFAULT_CENTER,
    isLocalDev: IS_LOCAL_DEV,
    defaultZipCode: isCA ? CA_DEFAULT_ZIPCODE : DEFAULT_ZIPCODE,
    userPreferredDealer,
  });

  const map = useGoogleMap({
    mapRef,
    center: mapCenter,
    googleAPILoaded,
    isInitializing,
    mapStyles: GoogleMapsStyles,
  });

  const handleMarkerClick = (dealer: DealersList, index: number) => {
    setSelectedDealer(dealer);
    setSelectedMarkerIndex(index);
  };

  const handleCloseCard = () => {
    setSelectedDealer(null);
    setSelectedMarkerIndex(-1);
  };

  useMapMarkers({
    map,
    dealers: dealersList,
    selectedIndex: selectedMarkerIndex,
    selectedIcon,
    unselectedIcon,
    onMarkerClick: handleMarkerClick,
    userPreferredDealer,
  });

  useEffect(() => {
    handleCloseCard();
  }, [dealersList, isLoadingDealers]);

  // The map stays mounted (hidden) while the empty state is shown so its init state survives; re-sync size when shown again
  useEffect(() => {
    if (!noDealersFound && map) {
      google.maps.event.trigger(map, 'resize');
    }
  }, [noDealersFound, map]);

  if (!googleAPILoaded || isInitializing) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', width: '100%', padding: '40px' }}>
        <Spinner />
      </div>
    );
  }

  return (
    <>
      {noDealersFound && !isLoadingDealers && (
        <NoDealersFound translationHandler={translationHandler} />
      )}
      <StyledMapContainer hidden={noDealersFound && !isLoadingDealers}>
      <StyledMapWrapper>
        <StyledMapRefContainer ref={mapRef} />
        {selectedDealer && (
          <StyledDealerCardWrapper>
            <DealerCard
              dealer={selectedDealer}
              onClose={handleCloseCard}
              onDealerSelect={onDealerSelect}
              authService={authService}
              translationHandler={translationHandler}
              trackingManager={trackingManager}
              localeCode={localeCode}
              locale={locale}
              fetchPreferredDealer={fetchPreferredDealer}
              userPreferredDealer={userPreferredDealer}
              showToast={showToast}
              userPreferredVehicle={userPreferredVehicle}
              history={history}
              currentPathname={currentPathname}
            />
          </StyledDealerCardWrapper>
        )}
      </StyledMapWrapper>
      </StyledMapContainer>
    </>
  );
};
